import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SettingsSection } from "./SettingsSection";

const CONFIRMATION_TEXT = "DELETE";

export const DangerZoneCard = () => {
  const { user, deleteAccount } = useAuth();
  const navigate = useNavigate();

  const [confirmInput, setConfirmInput] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await deleteAccount();
      navigate("/login");
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete account");
      setIsDeleting(false);
    }
  }, [deleteAccount, navigate]);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) {
      setConfirmInput("");
      setDeleteError(null);
    }
  }, []);

  if (!user) return null;

  return (
    <SettingsSection id="settings-danger" label="Danger zone">
      <p className="max-w-xl text-sm leading-6 text-zinc-600">
        Deleting your account signs you out and removes it across all GXL services:
        chat sessions are removed, Clipboard files are purged, and billing is
        canceled. Remaining stores are cleaned up in the background and you will
        receive a confirmation email.
      </p>
      <div className="mt-4">
        <Dialog open={open} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <button
              type="button"
              className="font-ui text-sm leading-6 text-red-700 transition-colors hover:text-red-900"
            >
              Delete account…
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogTitle>Delete account</DialogTitle>
            <DialogDescription>
              This action cannot be undone. Your account will be disabled immediately
              and you will be signed out. Chat, clipboard, and billing data are then
              deleted. You will receive a confirmation email when that finishes.
            </DialogDescription>

            <div className="space-y-4 py-2">
              <p className="text-sm text-zinc-600">
                To confirm, type <span className="font-mono font-semibold">{CONFIRMATION_TEXT}</span> below:
              </p>
              <label htmlFor="delete-account-confirm" className="sr-only">
                Type DELETE to confirm account deletion
              </label>
              <Input
                id="delete-account-confirm"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder={CONFIRMATION_TEXT}
                autoComplete="off"
                aria-invalid={deleteError ? true : undefined}
                aria-describedby={deleteError ? "delete-account-error" : undefined}
              />
              {deleteError && (
                <p id="delete-account-error" role="alert" className="text-sm text-red-700">
                  {deleteError}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <DialogClose asChild>
                <Button variant="outline" disabled={isDeleting}>
                  Cancel
                </Button>
              </DialogClose>
              <Button
                variant="destructive"
                onClick={handleDelete}
                loading={isDeleting}
                disabled={confirmInput !== CONFIRMATION_TEXT}
              >
                {isDeleting ? "Deleting..." : "Delete my account"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </SettingsSection>
  );
};
